/**
 * useParentMessages Hook - Real-time messages for parent users
 *
 * Features:
 * - Real-time listener for parent's in-app messages
 * - Filter by status, type, studentId
 * - Unread count tracking
 * - Mark messages as read
 * - School-scoped (hierarchical collection)
 *
 * @module features/messaging/hooks/useParentMessages
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '../../../firebase';
import MessageService from '../../../services/communication/MessageService';
import { MESSAGE_STATUSES } from '../../../models/Message';
import { useAuth } from '@/features/auth/hooks';
import { useSchool } from '../../../contexts/SchoolContext';

/**
 * Hook for managing parent messages
 *
 * @param {Object} options - Hook options
 * @param {number} [options.limit=50] - Max messages to fetch
 * @param {string} [options.status] - Filter by status ('read', 'delivered', etc.)
 * @param {string} [options.type] - Filter by message type
 * @param {string} [options.studentId] - Filter by specific student
 * @param {boolean} [options.unreadOnly=false] - Show only unread messages
 * @param {boolean} [options.autoSubscribe=true] - Auto-subscribe to real-time updates
 * @returns {Object} Hook state and methods
 */
export function useParentMessages({
  limit = 50,
  status = null,
  type = null,
  studentId = null,
  unreadOnly = false,
  autoSubscribe = true
} = {}) {
  const { user } = useAuth();
  const { schoolId } = useSchool();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener
  useEffect(() => {
    if (!user?.uid || !schoolId || !autoSubscribe) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Query hierarchical path: schools/{schoolId}/messages
      const messagesRef = collection(db, 'schools', schoolId, 'messages');

      // Build query constraints
      // NOTE: Keep query simple to avoid composite index requirements
      // Filter delivery.inApp.enabled client-side instead
      const constraints = [
        where('parentId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limit)
      ];

      // Apply optional filters
      if (status) {
        constraints.push(where('status', '==', status));
      }

      if (type) {
        constraints.push(where('type', '==', type));
      }

      if (studentId) {
        constraints.push(where('studentId', '==', studentId));
      }

      const q = query(messagesRef, ...constraints);

      // Subscribe to real-time updates
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          let fetchedMessages = [];

          snapshot.forEach((doc) => {
            const data = doc.data();

            // Add message with timestamps converted
            fetchedMessages.push({
              id: doc.id,
              ...data
            });
          });

          // Client-side filter: only in-app enabled messages
          fetchedMessages = fetchedMessages.filter(msg => msg.delivery?.inApp?.enabled);

          // Client-side filter for unread if needed
          if (unreadOnly) {
            fetchedMessages = fetchedMessages.filter(msg => !msg.delivery?.inApp?.readAt);
          }

          setMessages(fetchedMessages);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('[useParentMessages] Error fetching messages:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      console.log(`[useParentMessages] Subscribed to messages for parent ${user.uid} in school ${schoolId}`);

      // Cleanup listener on unmount
      return () => {
        console.log('[useParentMessages] Unsubscribing from messages');
        unsubscribe();
      };

    } catch (err) {
      console.error('[useParentMessages] Error setting up listener:', err);
      setError(err.message);
      setLoading(false);
    }

  }, [user?.uid, schoolId, limit, status, type, studentId, unreadOnly, autoSubscribe]);

  // Calculate unread count
  const unreadCount = messages.filter(
    msg => !msg.delivery?.inApp?.readAt
  ).length;

  /**
   * Mark a message as read on the in-app channel
   */
  const markAsRead = useCallback(async (messageId) => {
    if (!user?.uid || !schoolId) {
      throw new Error('User not authenticated or no school context');
    }

    try {
      const messageService = new MessageService();
      await messageService.markAsRead(messageId, 'inApp', schoolId);

      // Optimistic update
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? {
              ...msg,
              delivery: {
                ...msg.delivery,
                inApp: {
                  ...msg.delivery.inApp,
                  readAt: new Date()
                }
              },
              readCount: (msg.readCount || 0) + 1
            }
          : msg
      ));

    } catch (error) {
      console.error('[useParentMessages] Error marking message as read:', error);
      throw error;
    }
  }, [user?.uid, schoolId]);

  /**
   * Record a response to a message action
   */
  const recordResponse = useCallback(async (messageId, actionId, responseData = {}) => {
    if (!user?.uid || !schoolId) {
      throw new Error('User not authenticated or no school context');
    }

    try {
      const messageService = new MessageService();
      await messageService.recordActionResponse(
        messageId,
        'inApp',
        actionId,
        responseData,
        schoolId
      );

      // Optimistic update
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? {
              ...msg,
              delivery: {
                ...msg.delivery,
                inApp: {
                  ...msg.delivery.inApp,
                  respondedAt: new Date(),
                  response: { actionId, value: responseData, timestamp: new Date() }
                }
              },
              status: MESSAGE_STATUSES.RESPONDED,
              responseCount: (msg.responseCount || 0) + 1
            }
          : msg
      ));

    } catch (error) {
      console.error('[useParentMessages] Error recording response:', error);
      throw error;
    }
  }, [user?.uid, schoolId]);

  /**
   * Refresh messages manually
   */
  const refresh = useCallback(async () => {
    if (!user?.uid || !schoolId) return;

    setLoading(true);

    try {
      // Trigger re-subscription by updating state
      // (useEffect will handle the actual refresh)
      setError(null);
    } catch (err) {
      console.error('[useParentMessages] Error refreshing messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, schoolId]);

  return {
    messages,
    unreadCount,
    loading,
    error,
    markAsRead,
    recordResponse,
    refresh
  };
}

export default useParentMessages;
